import { IUsersInfo } from '@monetize/types/app';
import { useCallback, useMemo } from 'react';
import { MBox } from '../../../../../components/Monetize';
import { DataTableActions } from '../../../../../components/Monetize/DataTable/MDataTableActions';
import { useTableColumns } from '../../../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { USER_STATUS_DISPLAY_STRING } from '../../../../../constants/users';
import {
  TEAM_MEMBERS_CATALOG,
  TeamMembersColumnContext,
} from './teamMembers.columns';

/** Status is a SET filter; show the display string instead of the enum. */
const FILTER_DATA_DISPLAY_MAP = { status: USER_STATUS_DISPLAY_STRING };

interface TeamMembersTableProps {
  /** Active members sorted owner-first; filtering happens in the table. */
  members: IUsersInfo[];
  ownerId: string;
  isMutating: boolean;
  onRemoveMember: (member: IUsersInfo) => void;
}

/**
 * Members table for the team detail page. Filtering and sorting are both
 * browser-side (`clientControlled`), so the full member list is always passed
 * in.
 */
export const TeamMembersTable = ({
  members,
  ownerId,
  isMutating,
  onRemoveMember,
}: TeamMembersTableProps) => {
  const actionBodyTemplate = useCallback(
    (member: IUsersInfo) => {
      // The owner is removed by changing ownership from the page header.
      if (member.id === ownerId) {
        return null;
      }
      return (
        <DataTableActions
          actions={[
            {
              title: 'Remove',
              color: 'danger',
              enabled: true,
              isDisabled: isMutating,
              action: () => onRemoveMember(member),
            },
          ]}
        />
      );
    },
    [ownerId, isMutating, onRemoveMember],
  );

  const columnContext = useMemo<TeamMembersColumnContext>(
    () => ({ actionBodyTemplate, ownerId }),
    [actionBodyTemplate, ownerId],
  );
  const { columns, state: columnState } = useTableColumns(
    TEAM_MEMBERS_CATALOG,
    columnContext,
  );

  return (
    <MBox data-testid="team-members-table">
      <DataTable
        columns={columns}
        state={columnState}
        data={members}
        clientControlled
        filterDataDisplayMap={FILTER_DATA_DISPLAY_MAP}
        enablePagination={false}
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        emptyProps={{ mainMessage: 'No members yet.' }}
        emptyFilterResultsProps={{
          mainMessage: 'Looks like there are no members here.',
        }}
      />
    </MBox>
  );
};
